import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { observer } from 'mobx-react-lite';
import DropdownMenu from 'LearningManager/components/Dropdown';
import Carousel from 'LearningManager/components/Carousel';
import CourseCard from 'LearningManager/components/CourseCard';
import Heading from 'LearningManager/components/Heading';
import {
	PRIORITY_OPTIONS,
	STATUS_OPTIONS,
	TYPE_OPTIONS,
	CATEGORY_OPTIONS,
	ALL_FILTER_KEY,
	REQUIRED_FILTER_KEY,
	OPTIONAL_FILTER_KEY,
	FilterOption,
	CourseData,
	LearningPathData
} from 'LearningManager/constants/UserDashboard.constants';
import LearningPathCard from 'LearningManager/components/LearningPathCard';
import { formatCardDate } from 'LearningManager/helpers/date';
import { Icon } from 'Components';
import { useStores } from 'Hooks/useStore';
import { useDashboardContext } from 'Pages/Dashboard/DashboardContext';
import {
	ActivityAssignment,
	ActivityAssignmentFilters,
	ActivityAssignmentStatus,
	LearningPathAssignment
} from 'Services/Api/UserDashboard/Types';
import course1 from 'LearningManager/images/course-1.png';
import course2 from 'LearningManager/images/course-2.png';
import course3 from 'LearningManager/images/course-3.jpg';
import course4 from 'LearningManager/images/course-4.jpg';
import styles from './TrainingSection.module.scss';

const PLACEHOLDER_IMAGES = [course1, course2, course3, course4];

const filterLabel = (options: FilterOption[], key: string): string =>
	options.find((option) => option.key === key)?.label ?? '';

function filterValue<TValue extends string>(key?: string): TValue | undefined {
	return !key || key === ALL_FILTER_KEY ? undefined : (key as TValue);
}

interface TrainingSectionProps {
	filters: ActivityAssignmentFilters;
	onFiltersChange: (filters: ActivityAssignmentFilters) => void;
}

const STATUS_TAG: Record<ActivityAssignmentStatus, string> = {
	completed: 'Complete',
	in_progress: 'In Progress',
	not_started: 'Not started'
};

const formatDueDate = (dueDate?: string): string => {
	const formatted = formatCardDate(dueDate);
	return formatted ? formatted : '';
};

const ctaLabel = (status: ActivityAssignmentStatus): string => status === 'completed' ? 'learning.trainingSection.actions.review' : 'learning.trainingSection.actions.start'

const toCourseData = (
	activity: ActivityAssignment,
	imageIndex: number,
	t: TFunction
): CourseData => {
	const dueDate = activity.due_date ? formatDueDate(activity.due_date) : '';
	return {
		id: activity.id,
		title: activity.name,
		meta: dueDate ? t('learning.trainingSection.dueDate', { date: dueDate }) : '',
		tags: [
			STATUS_TAG[activity.status],
			activity.is_required ? 'Required' : 'Optional'
		],
		image: PLACEHOLDER_IMAGES[imageIndex % PLACEHOLDER_IMAGES.length],
		ctaLabel: t(ctaLabel(activity.status))
	}
};

const toLearningPathData = (
	lp: LearningPathAssignment,
	activitiesById: Record<number, ActivityAssignment>,
	t: TFunction
): LearningPathData => {
	const orderedActivities = [...lp.activities].sort(
		(a, b) => a.order - b.order
	);

	const courses = orderedActivities.map((ref, idx) => {
		const activity = activitiesById[ref.id];

		return activity
			? toCourseData(activity, idx, t)
			: {
					id: ref.id,
					title: t(
						'learning.trainingSection.activityFallback',
						{ id: ref.id }
					),
					meta: '',
					tags: [ref.is_required ? 'Required' : 'Optional'],
					image:
						PLACEHOLDER_IMAGES[
							idx % PLACEHOLDER_IMAGES.length
						],
					ctaLabel: t(
						'learning.trainingSection.actions.start'
					)
				};
	});

	const courseCount = lp.activities.length;

	const completedCount = lp.activities.filter(
		(ref) => activitiesById[ref.id]?.status === 'completed'
	).length;

	const completionPercentage = courseCount
		? Math.round((completedCount / courseCount) * 100)
		: 0;

	const statuses: string[] = [];
	if (lp.is_required) statuses.push('Required');
	if (courseCount > 0 && completedCount === courseCount) {
		statuses.push('Complete');
	} else if (completedCount > 0) {
		statuses.push('In Progress');
	}

	return {
		id: `lp-${lp.id}`,
		title: lp.name,
		statuses,
		courseCount,
		completedCount,
		completionPercentage,
		courses
	};
};

const TrainingSection: React.FC<TrainingSectionProps> = observer(
	(props) => {
		const { t } = useTranslation();
		const { filters, onFiltersChange } = props;

		const [category, setCategory] =
			useState<string>(ALL_FILTER_KEY);

		const {
			userDashboardStore,
			learningManagerStore,
			authStore
		} = useStores();

		const { activityTypes: categories } = learningManagerStore;
		const tenantPostfix =
			authStore.currentUserPoolData?.postfix;

		const { setToastInfo } = useDashboardContext();

		const {
			learningPathAssignments,
			activityAssignments,
			activityAssignmentsById
		} = userDashboardStore;

		const notifyLaunchFailure = (message: string) => {
			setToastInfo?.({ message, type: 'warning' });
		};

		const launch = async (activityId: number) => {
			const activity = activityAssignmentsById[activityId];

			if (!activity) {
				notifyLaunchFailure(
					t('learning.trainingSection.errors.courseUnavailable')
				);
				return;
			}

			const result = await userDashboardStore.launchActivity(
				activity,
				tenantPostfix
			);

			if (
				result === 'missing_system' ||
				result === 'missing_tenant' ||
				result === 'launch_failed'
			) {
				notifyLaunchFailure(
					t('learning.trainingSection.errors.courseUnavailable')
				);
			} else if (result === 'popup_blocked') {
				notifyLaunchFailure(
					t('learning.trainingSection.errors.popupBlocked')
				);
			}
		};

		const learningPaths = useMemo(
			() =>
				learningPathAssignments.map((lp) =>
					toLearningPathData(
						lp,
						activityAssignmentsById,
						t
					)
				),
			[learningPathAssignments, activityAssignmentsById, t]
		);

		const courseGroups = useMemo(() => {
			const toCards = (activities: ActivityAssignment[]) =>
				activities.map((activity, idx) =>
					toCourseData(activity, idx, t)
				);

			const categoryIds = new Set(
				categories.map((c) => c.id)
			);

			const groups = categories
				.map((c) => ({
					key: `category-${c.id}`,
					label: c.name,
					courses: toCards(
						activityAssignments.filter(
							(a) => a.category_id === c.id
						)
					)
				}))
				.filter((group) => group.courses.length > 0);

			const uncategorized = activityAssignments.filter(
				(a) => !categoryIds.has(a.category_id)
			);

			if (uncategorized.length) {
				groups.push({
					key: 'uncategorized',
					label: groups.length
						? t('learning.trainingSection.other')
						: '',
					courses: toCards(uncategorized)
				});
			}

			return groups;
		}, [categories, activityAssignments, t]);

		const priorityKey = filters.priority ?? ALL_FILTER_KEY;
		const statusKey = filters.status ?? ALL_FILTER_KEY;

		const typeKey =
			filters.is_required === undefined
				? ALL_FILTER_KEY
				: filters.is_required
					? REQUIRED_FILTER_KEY
					: OPTIONAL_FILTER_KEY;

		return (
			<div>
				<div className={styles.header}>
					<span className={styles.title}>
						{t('learning.trainingSection.title')}
					</span>

					<div className={styles.filters}>
						<DropdownMenu
							arrow={false}
							className={styles.filter}
							options={PRIORITY_OPTIONS}
							value={priorityKey}
							onChange={(key) =>
								onFiltersChange({
									...filters,
									priority: filterValue(key)
								})
							}
						>
							<div className={styles.filterTrigger}>
								<span className={styles.filterLabel}>
									{t(filterLabel(
										PRIORITY_OPTIONS,
										priorityKey
									))}
								</span>
								<Icon type="chevronDown" />
							</div>
						</DropdownMenu>

						<DropdownMenu
							arrow={false}
							className={styles.filter}
							options={STATUS_OPTIONS}
							value={statusKey}
							onChange={(key) =>
								onFiltersChange({
									...filters,
									status: filterValue(key)
								})
							}
						>
							<div className={styles.filterTrigger}>
								<span className={styles.filterLabel}>
									{t(filterLabel(
										STATUS_OPTIONS,
										priorityKey
									))}
								</span>
								<Icon type="chevronDown" />
							</div>
						</DropdownMenu>

						<DropdownMenu
							arrow={false}
							className={styles.filter}
							options={TYPE_OPTIONS}
							value={typeKey}
							onChange={(key) =>
								onFiltersChange({
									...filters,
									is_required:
										!key || key === ALL_FILTER_KEY
											? undefined
											: key === REQUIRED_FILTER_KEY
								})
							}
						>
							<div className={styles.filterTrigger}>
								<span className={styles.filterLabel}>
									{t(filterLabel(
										TYPE_OPTIONS,
										priorityKey
									))}
								</span>
								<Icon type="chevronDown" />
							</div>
						</DropdownMenu>

						<DropdownMenu
							arrow={false}
							className={styles.filter}
							options={CATEGORY_OPTIONS}
							value={category}
							onChange={(key) =>
								setCategory(key ?? ALL_FILTER_KEY)
							}
						>
							<div className={styles.filterTrigger}>
								<span className={styles.filterLabel}>
									{t(filterLabel(
										CATEGORY_OPTIONS,
										priorityKey
									))}
								</span>
								<Icon type="chevronDown" />
							</div>
						</DropdownMenu>
					</div>
				</div>

				<div className={styles.section}>
					<Heading
						icon={
							<Icon
								className={styles.icon}
								type="darkLP"
								role="img"
							/>
						}
						title={t(
							'learning.trainingSection.learningPathsTitle'
						)}
					/>

					<div className={styles.learningPathsWrapper}>
						{learningPaths.length ? (
							learningPaths.map((lp) => (
								<LearningPathCard
									key={lp.id}
									learningPath={lp}
									showCards={true}
									onCourseClick={(course) =>
										launch(course.id)
									}
								/>
							))
						) : (
							<div className={styles.emptyMessage}>
								{t(
									'learning.trainingSection.emptyLearningPaths'
								)}
							</div>
						)}
					</div>
				</div>

				<div className={styles.section}>
					<Heading
						icon={
							<Icon
								className={styles.icon}
								type="darkIndiCourses"
								role="img"
							/>
						}
						title={t(
							'learning.trainingSection.coursesTitle'
						)}
					/>

					<div className={styles.coursesWrapper}>
						{!courseGroups.length && (
							<div className={styles.emptyMessage}>
								{t(
									'learning.trainingSection.emptyCourses'
								)}
							</div>
						)}

						{courseGroups.map((group) => (
							<Carousel
								key={group.key}
								label={group.label || undefined}
								data={group.courses}
								itemsPerPage={4}
								renderItem={(course, index) => {
									const activity =
										activityAssignmentsById[course.id];

									const showReview =
										activity?.status === 'completed';

									return (
										<CourseCard
											index={index}
											htmlId={`lms-ud-course-${course.id}`}
											course={course}
											onClick={() =>
												launch(course.id)
											}
											ctaLabel={
												course.ctaLabel ??
												t(
													'learning.trainingSection.actions.start'
												)
											}
											ctaVariant={
												showReview
													? 'border'
													: undefined
											}
											ctaClassName={
												showReview
													? styles.reviewBtn
													: undefined
											}
											hideBadge
											hideRequiredSection
										/>
									);
								}}
							/>
						))}
					</div>
				</div>
			</div>
		);
	}
);

export default TrainingSection;